import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  ComponentType,
  FC,
  useMemo,
} from "react";
import { useMatch, useLocation } from "react-router-dom";
import AuthRequiredModal from "~@/components/layout/AuthRequiredModal";
import { useWebViewDetection } from "~@/hooks/useWebViewDetection";
import { jwtDecode } from "jwt-decode";
import { refreshAccessToken, subscribeAuthEvents } from "~@/api";
import { trackAnalyticsEvent } from "~@/analytics/amplitude";
import { ANALYTICS_EVENTS } from "~@/analytics/events";

const isProd = !process.env.REACT_APP_JUMP_API_ENDPOINT?.includes("dev");

interface JwtPayload {
  sub?: string;
  email?: string;
  name?: string;
  given_name?: string;
  preferred_username?: string;
  realm_access?: {
    roles?: string[];
  };
  exp?: number;
  iat?: number;
  jti?: string;
  iss?: string;
  aud?: string;
}

interface User {
  id: string;
  email: string;
  name?: string;
  username?: string;
  roles: string[];
  isDeactivated: boolean;
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, confirmationCode: string) => Promise<void>;
  sendVerificationCode: (email: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function buildUserFromDecoded(
  decoded: JwtPayload,
  overrides: Partial<User> = {},
): User {
  return {
    id: decoded.sub || "unknown",
    email: decoded.email || "unknown",
    name: decoded.name || decoded.given_name || undefined,
    username: decoded.preferred_username || undefined,
    roles: decoded.realm_access?.roles || [],
    isDeactivated: false,
    ...overrides,
  };
}

function getErrorPayload(error: unknown) {
  if (!error) return {};
  const e = error as any;
  return {
    status_code: e?.response?.status ?? null,
    error_message:
      e?.response?.data?.message ??
      e?.message ??
      (typeof error === "string" ? error : undefined),
  };
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = useMemo(() => !!user, [user]);

  const clearSession = useCallback((reason: "manual" | "expired") => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userData");
    localStorage.removeItem("hasVisited");
    setUser(null);
    setAuthToken(null);
    trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGOUT, { reason });
  }, []);

  const logout = useCallback(() => clearSession("manual"), [clearSession]);

  const loginWithToken = useCallback(async (token: string) => {
    trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_REQUEST, {
      method: "token",
    });
    try {
      setIsLoading(true);

      const decoded = jwtDecode<JwtPayload>(token);
      const currentTime = Math.floor(Date.now() / 1000);
      if (decoded.exp && decoded.exp < currentTime)
        throw new Error("Token expired");

      const userData = buildUserFromDecoded(decoded);

      localStorage.setItem("accessToken", token);
      localStorage.setItem("userData", JSON.stringify(userData));
      localStorage.setItem("hasVisited", "true");

      setUser(userData);
      setAuthToken(token);
      trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_SUCCESS, {
        method: "token",
        user_id: userData.id,
      });
    } catch (error) {
      console.error("Login with token error:", error);
      trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_FAILURE, {
        method: "token",
        ...getErrorPayload(error),
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sendVerificationCode = useCallback(async (email: string) => {
    trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_CODE_REQUEST, { email });
    try {
      const response = await fetch(
        isProd
          ? "https://auth.besau.kz/api/v2/auth/user/resend-verify-code"
          : "https://auth.dev.besau.kz/api/v2/auth/user/resend-verify-code",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(
          errorData.message || "Ошибка отправки кода",
        ) as any;
        error.response = { status: response.status, data: errorData };
        throw error;
      }

      trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_CODE_SUCCESS, { email });
    } catch (error) {
      console.error("Send verification code error:", error);
      if (error instanceof Error && !(error as any).response) {
        const networkError = error as any;
        networkError.response = { status: 0, data: { message: error.message } };
        throw networkError;
      }
      trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_CODE_FAILURE, {
        email,
        ...getErrorPayload(error),
      });
      throw error;
    }
  }, []);

  // Refreshes can start anywhere (startup check or the 401 interceptor);
  // the api layer reports them here so context state never goes stale.
  useEffect(
    () =>
      subscribeAuthEvents((event) => {
        if (event.type === "expired") {
          clearSession("expired");
          return;
        }

        try {
          const { data } = event;
          const decoded = jwtDecode<JwtPayload>(data.token);
          const userData = buildUserFromDecoded(decoded, {
            roles: data.roles || [],
            isDeactivated: data.isDeactivated || false,
          });
          localStorage.setItem("userData", JSON.stringify(userData));
          setUser(userData);
          setAuthToken(data.token);
        } catch (error) {
          console.error("Failed to apply refreshed token:", error);
          clearSession("expired");
        }
      }),
    [clearSession],
  );

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const userData = JSON.parse(localStorage.getItem("userData") || "null");

      if (!token || !userData) return;

      const decoded = jwtDecode<JwtPayload>(token);
      if (decoded.exp && decoded.exp < Math.floor(Date.now() / 1000)) {
        // State is updated via the "refreshed"/"expired" auth events
        await refreshAccessToken().catch((error) => {
          console.error("Refresh token failed:", error);
        });
        return;
      }

      setUser(userData);
      setAuthToken(token);
    } catch (error) {
      console.error("Auth check failed:", error);
      logout();
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  const login = useCallback(async (email: string, confirmationCode: string) => {
    trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_REQUEST, {
      email,
      method: "code",
    });
    try {
      setIsLoading(true);

      const response = await fetch(
        isProd
          ? "https://auth.besau.kz/api/v2/auth/user/verify"
          : "https://auth.dev.besau.kz/api/v2/auth/user/verify",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, confirmationCode }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        const decoded = jwtDecode<JwtPayload>(data.token);
        const userData = buildUserFromDecoded(decoded, {
          id: decoded.sub || email,
          email: decoded.email || email,
          roles: data.roles || [],
          isDeactivated: data.isDeactivated || false,
        });

        localStorage.setItem("accessToken", data.token);
        localStorage.setItem("refreshToken", data.refreshToken);
        localStorage.setItem("userData", JSON.stringify(userData));
        localStorage.setItem("hasVisited", "true");
        setUser(userData);
        setAuthToken(data.token);
        trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_SUCCESS, {
          method: "code",
          email,
          user_id: userData.id,
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(
          errorData.message || "Неверный код подтверждения",
        ) as any;
        error.response = { status: response.status, data: errorData };
        throw error;
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error instanceof Error && !(error as any).response) {
        const networkError = error as any;
        networkError.response = { status: 0, data: { message: error.message } };
        throw networkError;
      }
      trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_FAILURE, {
        method: "code",
        email,
        ...getErrorPayload(error),
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        login,
        loginWithToken,
        sendVerificationCode,
        logout,
        checkAuth,
        token: authToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

const AuthWrapper: React.FC<{
  children: React.ReactElement;
  LoadingComponent: () => React.ReactElement;
}> = ({ children, LoadingComponent }) => {
  const { isAuthenticated, isLoading, loginWithToken } = useAuth();
  const { isWebView } = useWebViewDetection();
  const location = useLocation();
  const match1 = useMatch("/");
  const match2 = useMatch("/schedule");

  const tokenFromQuery = new URLSearchParams(location.search).get("token");

  useEffect(() => {
    if (isWebView && tokenFromQuery) {
      loginWithToken(tokenFromQuery).catch((error) => {
        console.error("Failed to login with token:", error);
      });
    }
  }, [isWebView, tokenFromQuery, loginWithToken]);

  if (isLoading) return <LoadingComponent />;

  if (!isAuthenticated) {
    const hasVisited = localStorage.getItem("hasVisited");
    if (!hasVisited) {
      localStorage.setItem("hasVisited", "true");
      return children;
    }
    return (
      <>
        {children}
        <AuthRequiredModal
          open={!match1 && !match2}
          onLogin={() => (window.location.href = "/login")}
        />
      </>
    );
  }

  return children;
};

export const withAuthenticationRequired = <P extends object>(
  Component: ComponentType<P>,
  options: { LoadingComponent?: () => React.ReactElement } = {},
): FC<P> => {
  const WithAuthenticationRequired: FC<P> = (props) => {
    const LoadingComponent =
      options.LoadingComponent ??
      (() => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          Загрузка...
        </div>
      ));

    return (
      <AuthWrapper LoadingComponent={LoadingComponent}>
        <Component {...props} />
      </AuthWrapper>
    );
  };

  WithAuthenticationRequired.displayName = `withAuthenticationRequired(${Component.displayName || Component.name})`;
  return WithAuthenticationRequired;
};
